import React from 'react'
import clsx from 'clsx'
import {
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Badge,
} from '@material-ui/core'
import MuiListItem from '@material-ui/core/ListItem'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { NavLink } from 'react-router-dom'
import {
  drawerWidth,
  desktopRailWidth,
  NAV_ICON_CENTER,
  NAV_LABEL_LEFT,
} from './styles'

const ICON_CLASS = 'AppShellSidebarItem-icon'

const ITEM_INSET = 12
const ICON_SIZE = 20
const ITEM_HEIGHT = 44
const ITEM_GAP = 4
const COLLAPSED_ITEM_SIZE = 48

// Icons sit on the same vertical centre line as the toolbar's brand-switcher
// button, and labels start on the same left edge as the logo, whether the
// item is a full-width pill or a rail-sized one.
const EXPANDED_PADDING_LEFT = NAV_ICON_CENTER - ICON_SIZE / 2 - ITEM_INSET
const ICON_BOX_WIDTH = NAV_LABEL_LEFT - (NAV_ICON_CENTER - ICON_SIZE / 2)

const reducedMotion = '@media (prefers-reduced-motion: reduce)'

// withStyles (not styled) is required here: MUI v4's styled() intercepts
// the polymorphic `component` prop and renders it directly, bypassing
// MuiListItem entirely. withStyles preserves MuiListItem's own rendering,
// including ButtonBase composition when `button` is true.
const ExtendedListItem = withStyles(theme => ({
  root: {
    color: theme.palette.sidebar.itemText,
    borderRadius: theme.palette.sidebar.itemBorderRadius,
    boxSizing: 'border-box',
    marginLeft: ITEM_INSET,
    marginRight: ITEM_INSET,
    marginBottom: ITEM_GAP,
    // A fixed width keeps labels from reflowing while the drawer paper
    // animates between rail and full width.
    width: drawerWidth - ITEM_INSET * 2,
    minHeight: ITEM_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: EXPANDED_PADDING_LEFT,
    paddingRight: 16,
    overflow: 'hidden',
    transition: theme.transitions.create(
      ['background-color', 'background-image', 'width', 'margin', 'padding'],
      {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      },
    ),
    [reducedMotion]: { transition: 'none' },
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
    // M3 focus state layer: 12% on-secondary-container.
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.focusOverlay,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.sidebar.activeBg,
      color: theme.palette.sidebar.itemText,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.sidebar.activeBg,
    },
    // When selected and keyboard-focused, layer the focus overlay on
    // top of the active background so the focus ring is still visible.
    '&.Mui-selected.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.activeBg,
      backgroundImage: `linear-gradient(${theme.palette.sidebar.focusOverlay}, ${theme.palette.sidebar.focusOverlay})`,
    },
  },
}))(MuiListItem)

const useItemStyles = makeStyles(theme => ({
  collapsedItem: ({ railWidth }) => ({
    width: COLLAPSED_ITEM_SIZE,
    marginLeft: (railWidth - COLLAPSED_ITEM_SIZE) / 2,
    marginRight: (railWidth - COLLAPSED_ITEM_SIZE) / 2,
    paddingLeft: (COLLAPSED_ITEM_SIZE - ICON_SIZE) / 2,
    paddingRight: (COLLAPSED_ITEM_SIZE - ICON_SIZE) / 2,
  }),
  iconWrapper: {
    color: 'inherit',
    minWidth: ICON_BOX_WIDTH,
    [`& .${ICON_CLASS}`]: {
      height: ICON_SIZE,
      width: ICON_SIZE,
      fontSize: ICON_SIZE,
    },
  },
  label: {
    margin: 0,
    minWidth: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
    [reducedMotion]: { transition: 'none' },
  },
  labelHidden: {
    opacity: 0,
  },
  annotationHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  iconBadge: {
    top: 0,
    right: -2,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    fontSize: 10,
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
  },
}))

// No `noWrap` ellipsis: it sets overflow hidden, which would clip count badges
// that hang off the label's corner (see InlineCountBadge).
const labelTypographyProps = {
  style: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
}

/**
 * Shared pill used by every sidebar entry: link items, and the collapse
 * toggle. Keeps one DOM structure across expanded/collapsed so the width,
 * margin and label-opacity changes animate instead of remounting.
 */
export function SidebarItemShell({
  icon,
  label,
  tooltip,
  collapsed = false,
  railWidth = desktopRailWidth,
  iconBadgeContent = 0,
  annotation,
  className,
  ...listItemProps
}) {
  const classes = useItemStyles({ railWidth })
  const iconComponent = React.cloneElement(icon, { className: ICON_CLASS })
  const showIconBadge = iconBadgeContent > 0

  const tooltipTitle = collapsed
    ? tooltip || (typeof label === 'string' ? label : '')
    : ''

  return (
    <Tooltip title={tooltipTitle} placement="right" enterDelay={200}>
      <ExtendedListItem
        button
        className={clsx(className, { [classes.collapsedItem]: collapsed })}
        {...listItemProps}
      >
        <ListItemIcon className={classes.iconWrapper}>
          {showIconBadge ? (
            <Badge
              color="default"
              badgeContent={iconBadgeContent}
              invisible={!collapsed}
              classes={{ badge: classes.iconBadge }}
            >
              {iconComponent}
            </Badge>
          ) : (
            iconComponent
          )}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={labelTypographyProps}
          className={clsx(classes.label, {
            [classes.labelHidden]: collapsed,
          })}
        />
        {annotation && (
          <ListItemSecondaryAction
            className={clsx({ [classes.annotationHidden]: collapsed })}
          >
            {annotation}
          </ListItemSecondaryAction>
        )}
      </ExtendedListItem>
    </Tooltip>
  )
}

export default function SidebarItem({
  icon,
  onClick,
  label,
  path,
  location,
  exact,
  annotation,
  tooltip,
  disabled = false,
  external = false,
  hide = false,
  collapsed = false,
  railWidth,
  iconBadgeContent = 0,
  isSelected,
}) {
  const isBookingsPage =
    path.includes('bookings') &&
    location.pathname.includes('bookings') &&
    !location.pathname.includes('schedule') &&
    !path.includes('schedule') &&
    !location.pathname.includes('events')
  const isEventPage =
    path.includes('schedule') &&
    location.pathname.includes('events') &&
    !location.pathname.includes('offerings') &&
    !path.includes('offerings')

  const selected =
    typeof isSelected === 'function'
      ? isSelected({ path, location, exact })
      : exact
        ? `${path}/` === location.pathname ||
        path === location.pathname ||
        isBookingsPage ||
        isEventPage
        : location.pathname.startsWith(path)
  const linkProps = !external
    ? {
      component: NavLink,
      to: path,
      ...(typeof isSelected === 'function'
        ? {
          isActive: (match, location) => isSelected({ path, location, exact }),
        }
        : {}),
    }
    : { component: 'a', href: path, target: external.target || '_blank' }

  if (hide) return null

  return (
    <SidebarItemShell
      icon={icon}
      label={label}
      tooltip={tooltip}
      collapsed={collapsed}
      railWidth={railWidth}
      iconBadgeContent={iconBadgeContent}
      annotation={annotation}
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      aria-current={selected ? 'page' : undefined}
      {...linkProps}
    />
  )
}
